// 播放视图：按需加载 Ruffle，🏠 返回 + ⛶ 全屏 + 大号开始遮罩。
import type { Manifest, StoryInfo } from "../../shared/manifest";
import { PATHS } from "../../shared/navigation";
import {
  el,
  ensureRuffle,
  loadManifest,
  mediaUrl,
  topbar,
  type RufflePlayerElement,
} from "../lib";

const VOLUME_KEY = "aesop-volume";

/** 页面级主音量(0–1),由播放页的 🔊 按钮/滑杆维护,跨故事与下次访问记忆。 */
let currentVolume = 1;

function readSavedVolume(): number {
  const raw = localStorage.getItem(VOLUME_KEY);
  if (raw === null) return 1; // 未设置过 → 默认全音量(Number(null)===0,不能直接转)
  const v = Number(raw);
  return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
}

function findStory(manifest: Manifest, id: string | null): StoryInfo | null {
  if (!id) return null;
  return (
    manifest.stories.find((s) => s.id === id) ??
    manifest.stories.find((s) => s.dir === id) ??
    null
  );
}

export async function playerView(root: HTMLElement): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");
  const autostart = params.get("autostart") === "1";

  const stage = el("div");
  stage.id = "stage";
  const loadingNote = el("div", "loading-note", "正在准备……");
  loadingNote.id = "loadingNote";
  const overlay = el("div", "start-overlay");
  overlay.id = "startOverlay";
  const inner = el("div", "inner");
  const startBtn = el("button", "btn-play") as HTMLButtonElement;
  startBtn.type = "button";
  startBtn.id = "startBtn";
  startBtn.setAttribute("aria-label", "开始播放");
  startBtn.textContent = "▶";
  const overlayName = el("h2", "overlay-name", "…");
  overlayName.id = "overlayName";
  inner.appendChild(startBtn);
  inner.appendChild(overlayName);
  inner.appendChild(el("p", "sub1", "点一下开始，打开声音"));
  overlay.appendChild(inner);
  stage.appendChild(loadingNote);
  stage.appendChild(overlay);

  // 顶栏：面包屑 + 标题 + 全屏按钮
  const fullscreenBtn = el("button", "btn-fullscreen") as HTMLButtonElement;
  fullscreenBtn.type = "button";
  fullscreenBtn.textContent = "⛶";
  fullscreenBtn.setAttribute("aria-label", "全屏");
  fullscreenBtn.addEventListener("click", () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void stage.requestFullscreen?.();
    }
  });

  const title = el("span", "story-title", "…");
  const headRight = el("div", "head-right");

  // 音量控制:🔇/🔊 切换 + 滑杆调节(Ruffle 主音量,不改 SWF 内容)
  currentVolume = readSavedVolume();
  let lastNonZero = currentVolume > 0 ? currentVolume : 0.5;
  const volumeWrap = el("div", "volume-wrap");
  const muteBtn = el("button", "btn-vol") as HTMLButtonElement;
  muteBtn.type = "button";
  muteBtn.setAttribute("aria-label", "静音/取消静音");
  const volSlider = document.createElement("input");
  volSlider.type = "range";
  volSlider.className = "volume-slider";
  volSlider.min = "0";
  volSlider.max = "100";
  volSlider.step = "5";
  volSlider.setAttribute("aria-label", "音量");

  const applyVolume = (v: number): void => {
    currentVolume = v;
    const p = window.__currentPlayer;
    if (p) p.volume = v;
    try {
      localStorage.setItem(VOLUME_KEY, String(v));
    } catch {
      /* 忽略 */
    }
    muteBtn.textContent = v === 0 ? "🔇" : "🔊";
    volSlider.value = String(Math.round(v * 100));
  };
  applyVolume(currentVolume);

  muteBtn.addEventListener("click", () => {
    if (currentVolume > 0) {
      lastNonZero = currentVolume;
      applyVolume(0);
    } else {
      applyVolume(lastNonZero);
    }
  });
  volSlider.addEventListener("input", () => {
    applyVolume(Number(volSlider.value) / 100);
  });

  volumeWrap.appendChild(muteBtn);
  volumeWrap.appendChild(volSlider);

  headRight.appendChild(title);
  headRight.appendChild(volumeWrap);
  headRight.appendChild(fullscreenBtn);

  root.appendChild(
    topbar(
      [
        { label: "英语", href: PATHS.english },
        { label: "伊索寓言", href: PATHS.aesop },
        { label: "播放" },
      ],
      headRight
    )
  );
  root.appendChild(stage);

  let manifest: Manifest;
  try {
    manifest = await loadManifest();
  } catch (err) {
    title.textContent = "清单加载失败";
    showError(stage, "清单加载失败：" + ((err as Error).message ?? String(err)) + "。请稍后重试。");
    return;
  }
  if (!stage.isConnected) return; // 用户已离开播放页：中止，避免幽灵播放器

  const story = findStory(manifest, id);
  if (!story) {
    title.textContent = "未找到该故事";
    showError(stage, "未找到对应故事。回到列表重新选一个吧。");
    return;
  }

  document.title = `${story.id} ${story.title} · 小朋友学习乐园`;
  title.textContent = `${story.id} ${story.title}`;
  overlayName.textContent = `${story.id} ${story.title}`;

  const begin = (): void => {
    void startPlayback(stage, story);
  };
  if (autostart) {
    begin();
  } else {
    startBtn.addEventListener("click", begin);
  }
}

function showError(stage: HTMLElement, msg: string): void {
  stage.appendChild(el("div", "error-note", msg));
  document.getElementById("startOverlay")?.remove();
  document.getElementById("loadingNote")?.remove();
}

async function startPlayback(stage: HTMLElement, story: StoryInfo): Promise<void> {
  document.getElementById("startOverlay")?.remove();
  document.getElementById("loadingNote")?.remove();

  const ok = await ensureRuffle();
  if (!stage.isConnected) return; // 加载 Ruffle 期间用户已离开：中止（否则幽灵播放器会在后台出声）
  const ruffle = ok ? window.RufflePlayer?.newest() : undefined;
  if (!ruffle) {
    showError(stage, "Ruffle 未正确加载：请确认 ruffle/ruffle.js 可访问，并用 http(s) 打开本页。");
    return;
  }

  const src = mediaUrl(`stories/${story.dir}/${story.entry}`);
  const player: RufflePlayerElement = ruffle.createPlayer({
    autoplay: "on",
    unmuteOverlay: "hidden",
    letterbox: "on",
    openUrlMode: "allow",
    scale: "showAll",
  });
  player.style.width = "100%";
  player.style.height = "100%";
  player.volume = currentVolume; // 恢复上次音量
  stage.appendChild(player);

  window.__currentPlayer = player;
  window.__currentStory = story;
  window.__currentSrc = src;

  player
    .ruffle()
    .load(src)
    .catch((err: unknown) => {
      showError(stage, "SWF 加载失败：" + src + "\n" + ((err as Error)?.message ?? String(err)));
    });
}
